// ============================================================
// estimates.js
// Estimates on a quote or invoice: the data shape, the conversion of older
// documents, and THE import both document pages use when an estimator returns.
//
// ONE import, shared. It used to be written out twice — in NewDocument and in
// EditDocument — and two copies of the same rule are how the round-up bug and
// the seventeen sidebar widths happened. Change the import here, once.
//
// No React, no Firebase, no side effects.
// ============================================================

// ── THE SHAPE ────────────────────────────────────────────────────────
//
// A document carries
//
//   estimates: [{ id, kind, specs, label }]
//     id     stable, unique within the document, generated when the estimate
//            is first imported and never changed after
//     kind   'chainlink' | 'wood' | 'vinyl' | 'aluminum'
//     specs  that estimator's own settings object, exactly as it built it
//     label  a short human summary, e.g. "Chain link · 4' Residential · 180 ft"
//
// and every line an estimate produced carries that estimate's `estimateId`.
// That id is what lets two chain link estimates live on one document: before
// it, their lines were identical ({ fromEstimator, estimatorKind: 'chainlink' })
// and a second import could only replace the first.
//
// A line with fromEstimator but NO estimateId is "unattached": it still renders
// and counts as an estimator line, but belongs to no estimate and nothing can
// modify it. Older documents produce these (see readEstimates).

export const ESTIMATE_KINDS = ['chainlink', 'wood', 'vinyl', 'aluminum'];

// Which estimator a settings object came from. Wood, vinyl and aluminum have
// always written `kind`; chain link now writes kind: 'chainlink' too, but its
// older settings do not, so anything unrecognised — including no kind at all —
// is chain link. That fallback exists for OLD data only.
export const estimateKindOf = (specs) => (ESTIMATE_KINDS.includes(specs?.kind) ? specs.kind : 'chainlink');

export const newEstimateId = () =>
  `est_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// ── LABEL ────────────────────────────────────────────────────────────

const KIND_NAME = { chainlink: 'Chain link', wood: 'Wood', vinyl: 'Vinyl', aluminum: 'Aluminum' };
const num = (v) => parseFloat(v) || 0;
const trim2 = (n) => String(Math.round(n * 100) / 100);
const feet = (v) => (num(v) > 0 ? `${trim2(num(v))}'` : '');
// 'fullPrivacy' → 'Full privacy'
const humanize = (s) => {
  const words = String(s || '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().trim();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
};

// "Chain link · 4' Residential · 180 ft", "Wood · 6' Full privacy · 120 ft · 1 gate",
// "Vinyl · 96 ft · 2 gates". Built only from fields the settings object already
// has; anything missing is simply left out.
export function estimateLabel(kind, specs) {
  const parts = [KIND_NAME[kind] || 'Estimate'];
  if (kind === 'chainlink') {
    const finish = specs?.finish && specs.finish !== 'Galvanized' ? specs.finish : '';
    const desc = [feet(specs?.height), specs?.fenceClass || '', finish].filter(Boolean).join(' ');
    if (desc) parts.push(desc);
  } else if (kind === 'wood') {
    const desc = [feet(specs?.height), humanize(specs?.style)].filter(Boolean).join(' ');
    if (desc) parts.push(desc);
  }
  const runs = Array.isArray(specs?.runs) ? specs.runs : [];
  // Wood runs are measured in `feet`; the other three estimators use `length`.
  const totalFt = runs.reduce((s, r) => s + num(kind === 'wood' ? r?.feet : r?.length), 0);
  if (totalFt > 0) parts.push(`${trim2(totalFt)} ft`);
  const gates = Array.isArray(specs?.gates) ? specs.gates.length : 0;
  if (gates > 0) parts.push(`${gates} gate${gates === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// ── OLDER DOCUMENTS ──────────────────────────────────────────────────
//
// Read a document's (or a session draft's) estimates in the new shape, without
// touching what is stored — the next save writes the new shape.
//
//   `estimates` present      used as-is (tidied: a missing kind or label is
//                            filled in). Lines are left exactly as they are.
//   only `estimatorSpecs`    the old single object becomes a one-item list.
//                            Its kind is read from the settings, chain link
//                            when absent. Estimator lines OF THAT MATERIAL with
//                            no estimateId are adopted into it — they are the
//                            lines today's Modify would have replaced, so
//                            adopting them keeps that behaviour. Estimator lines
//                            of any OTHER material stay unattached.
//   neither                  no estimates.
//
// The adopted estimate's id is deterministic ('legacy-chainlink', …) rather
// than random, so opening the same unsaved old document twice gives the same
// id both times.
export function readEstimates({ estimates, estimatorSpecs, lineItems } = {}) {
  const lines = Array.isArray(lineItems) ? lineItems : [];

  if (Array.isArray(estimates)) {
    return {
      estimates: estimates
        .filter(e => e && e.id)
        .map(e => {
          const kind = ESTIMATE_KINDS.includes(e.kind) ? e.kind : estimateKindOf(e.specs);
          return { id: e.id, kind, specs: e.specs ?? null, label: e.label || estimateLabel(kind, e.specs) };
        }),
      lineItems: lines,
    };
  }

  if (estimatorSpecs && typeof estimatorSpecs === 'object') {
    const kind = estimateKindOf(estimatorSpecs);
    const id = `legacy-${kind}`;
    return {
      estimates: [{ id, kind, specs: estimatorSpecs, label: estimateLabel(kind, estimatorSpecs) }],
      lineItems: lines.map(item => (
        item && item.fromEstimator && !item.estimateId && (item.estimatorKind || 'chainlink') === kind
          ? { ...item, estimateId: id }
          : item
      )),
    };
  }

  return { estimates: [], lineItems: lines };
}

// ── WHAT THE DOCUMENT SHOWS AND SAVES ────────────────────────────────

// The most recently ADDED estimate: the last in the list. A modify replaces an
// entry where it stands, so it does not move an estimate to the end.
export const latestEstimate = (estimates) =>
  (Array.isArray(estimates) && estimates.length ? estimates[estimates.length - 1] : null);

// Remove ONE estimate: its entry and every line carrying its estimateId. Every
// other estimate, every manual line and every unattached line is left as it is.
export function removeEstimate({ lineItems, estimates } = {}, estimateId) {
  return {
    lineItems: (Array.isArray(lineItems) ? lineItems : []).filter(i => !(i && i.estimateId === estimateId)),
    estimates: (Array.isArray(estimates) ? estimates : []).filter(e => e && e.id !== estimateId),
  };
}

// ⚠️ A DERIVED MIRROR, NOT TRUTH. The old single `estimatorSpecs` field is still
// written during the transition, holding the most recent estimate's settings,
// so anything still reading it keeps working. `estimates` is the record; never
// read estimatorSpecs back as if it were.
export const estimatorSpecsMirror = (estimates) => latestEstimate(estimates)?.specs ?? null;

// ── THE ESTIMATOR ROUND TRIP ─────────────────────────────────────────
//
// The estimator pages are untouched: they return { lineItems, estimatorSpecs,
// priceLevel } as they always have, and know nothing about estimate ids. What
// the trip was FOR is recorded on the document side instead, in the session
// draft each page already saves before it navigates to an estimator:
//
//   { mode: 'add' }                              a new estimate
//   { mode: 'modify', estimateId, kind }         replace that one estimate
export const PENDING_ADD = Object.freeze({ mode: 'add' });
export const pendingModify = (estimate) =>
  (estimate ? { mode: 'modify', estimateId: estimate.id, kind: estimate.kind } : PENDING_ADD);

// A fingerprint of one estimator return, from its incoming line ids — every
// calculation stamps fresh random ids, so two returns never share one. The page
// keeps the fingerprints it has already applied in its draft, so revisiting the
// same history entry (browser Back) cannot import the same estimate twice. Null
// when the lines carry no ids, in which case nothing is de-duplicated.
export const estimatorImportKey = (incomingItems) => {
  const ids = (incomingItems || []).map(i => i && i.id).filter(v => v !== undefined && v !== null);
  return ids.length ? ids.join('|') : null;
};

// ── THE IMPORT ───────────────────────────────────────────────────────
//
// Apply an estimator's return to a document's lines and estimates.
//
//   ADD     a new estimate. Its lines are appended, a new entry is appended to
//           estimates. Nothing else is touched — a second chain link estimate
//           no longer replaces the first.
//
//   MODIFY  the estimate named by `pending.estimateId`. ONLY lines carrying that
//           estimateId are removed, the new lines are appended, and only that
//           entry is replaced (in place, same id). Every other estimate and
//           every other line is left exactly as it was.
//           Lines of that estimate the user edited by hand are replaced along
//           with the rest — the owner's decision; there is deliberately no
//           preservation logic.
//
//   A modify is honoured only when that estimate still exists AND is the same
//   material the estimator returned. Otherwise it is treated as an add, which
//   can never remove anything.
//
//   No settings at all (the standalone results page imports lines without
//   them): the lines are appended unattached. With nothing to re-open, they are
//   not an estimate.
//
// Each incoming line is rebuilt field by field — the same fields, product
// lookup and pricing as before — with estimateId stamped on it.
//
// Returns { lineItems, estimates, estimateId, mode }.
export function applyEstimatorImport({
  lineItems, estimates, incomingItems, incomingSpecs, pending, products, priceLevel, priceForLevel,
}) {
  const lines = Array.isArray(lineItems) ? lineItems : [];
  const list = Array.isArray(estimates) ? estimates : [];
  const productList = Array.isArray(products) ? products : [];
  const hasSpecs = !!incomingSpecs && typeof incomingSpecs === 'object';
  const incomingKind = estimateKindOf(incomingSpecs);

  const target = hasSpecs && pending && pending.mode === 'modify'
    ? list.find(e => e.id === pending.estimateId && e.kind === incomingKind) || null
    : null;
  const mode = !hasSpecs ? 'unattached' : (target ? 'modify' : 'add');
  const estimateId = mode === 'modify' ? target.id : (mode === 'add' ? newEstimateId() : null);

  const rebuilt = (incomingItems || []).map((item, index) => {
    const product = item.productId
      ? productList.find(p => p.id === item.productId)
      : (item.sku && item.sku !== '—' && item.sku !== 'NON-STOCK'
          ? productList.find(p => p.sku === item.sku && p.isActive)
          : null);
    return {
      id: Date.now() + Math.random() + index,
      sku: item.sku || '—',
      description: product?.description || item.description,
      unit: item.unit || product?.unit || 'pc',
      qty: item.qty,
      unitPrice: product ? priceForLevel(product, priceLevel) : (item.unitPrice || 0),
      isStock: item.isStock ?? product?.isStock ?? true,
      productId: product?.id || item.productId || null,
      fromEstimator: true,
      estimatorKind: item.estimatorKind || incomingKind,
      ...(estimateId ? { estimateId } : {}),
    };
  });

  if (mode === 'modify') {
    return {
      lineItems: [...lines.filter(i => !(i && i.estimateId === estimateId)), ...rebuilt],
      estimates: list.map(e => (e.id === estimateId
        ? { id: e.id, kind: incomingKind, specs: incomingSpecs, label: estimateLabel(incomingKind, incomingSpecs) }
        : e)),
      estimateId,
      mode,
    };
  }
  if (mode === 'add') {
    return {
      lineItems: [...lines, ...rebuilt],
      estimates: [...list, { id: estimateId, kind: incomingKind, specs: incomingSpecs, label: estimateLabel(incomingKind, incomingSpecs) }],
      estimateId,
      mode,
    };
  }
  return { lineItems: [...lines, ...rebuilt], estimates: list, estimateId: null, mode };
}
